import React, { useCallback, useMemo, useState } from "react";

import { getLastConnectedStatusMeta } from "cortex/ui/src/constant/lastConnected";
import DeviceDetailsDrawer from "cortex/ui/src/fleet/view/device/component/DeviceDetailsDrawer";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { useAppSelector } from "cortex/ui/src/hooks";
import {
  devicesSelector,
  getPoint,
  hasPoint,
} from "cortex/ui/src/redux-store/devices/selectors";
import { devicesLastSeenSelector } from "cortex/ui/src/redux-store/last-seen-devices/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import { Map } from "cortex/ui/src/ui/map";
import type { MarkerStatus } from "cortex/ui/src/ui/map/utils/action";

const getAllDevicesAsArray = makeGetAllAsArray<Device>();

function getMarkers(
  devices: ReadonlyArray<Device>,
  devicesLastSeen: Record<string, { last_seen?: string | null }>,
) {
  return devices
    .filter((device) => hasPoint(device))
    .map((device) => {
      const coords = getPoint(device);
      if (!coords) {
        return null;
      }
      const lastSeen = {
        isHibernating: device.status?.hibernating ?? false,
        neverConnected: !device.has_connected,
        value: devicesLastSeen[device.id]?.last_seen ?? null,
      };
      const markerStatus = getLastConnectedStatusMeta(
        lastSeen,
      ) as unknown as MarkerStatus;

      return {
        device: { ...device },
        coords,
        markerStatus,
        showInfoWindow: true,
      };
    })
    .filter((marker): marker is NonNullable<typeof marker> => marker !== null);
}

interface DevicesMapProps {
  devices?: ReadonlyArray<Device>;
  minZoom?: number;
  maxZoom?: number;
}

interface DeviceDrawerState {
  isOpen: boolean;
  selectedDevice: Device | null;
}

export default function DevicesMap({
  devices: propDevices,
  minZoom = 2,
  maxZoom = 16,
}: DevicesMapProps) {
  const devicesById = useAppSelector(devicesSelector);
  const [deviceDrawerState, setDeviceDrawerState] = useState<DeviceDrawerState>(
    {
      isOpen: false,
      selectedDevice: null,
    },
  );
  const allDevices = getAllDevicesAsArray({
    byId: devicesById,
  });

  // Use devices from props if provided; otherwise, use all devices from the store
  const devicesToShow = propDevices ?? allDevices;
  const devicesLastSeen = useAppSelector(devicesLastSeenSelector);

  const markers = getMarkers(devicesToShow, devicesLastSeen);
  const closeDeviceDetailsDrawer = useCallback(() => {
    setDeviceDrawerState({
      isOpen: false,
      selectedDevice: null,
    });
  }, []);

  const openDeviceDetailsDrawer = useCallback(
    (deviceId: string) => {
      const selectedDevice =
        devicesToShow.find((device) => device.device_id === deviceId) ?? null;

      if (!selectedDevice) {
        return;
      }

      setDeviceDrawerState({
        isOpen: true,
        selectedDevice,
      });
    },
    [devicesToShow],
  );

  const drawerTitle = useMemo(() => {
    return deviceDrawerState.selectedDevice?.name || "Device Details";
  }, [deviceDrawerState.selectedDevice]);

  return (
    <>
      <Map
        markers={markers}
        maxZoom={maxZoom}
        minZoom={minZoom}
        onViewDeviceDetails={openDeviceDetailsDrawer}
      />
      {deviceDrawerState.selectedDevice && (
        <DeviceDetailsDrawer
          device={deviceDrawerState.selectedDevice}
          onClose={closeDeviceDetailsDrawer}
          open={deviceDrawerState.isOpen}
          title={drawerTitle}
        />
      )}
    </>
  );
}
